'use client';

import {
  type ReactElement,
  type ReactNode,
  type SyntheticEvent,
  useEffect,
  useMemo,
  useRef,
  type FormEventHandler
} from 'react';

import type {
  IForm,
  IFormErrors,
  IInvalidProperty
} from '@/react/view-models/Form';
import UserInteractionService, {
  type InteractionDetails
} from '@/services/isomorphic/UserInteractionService';
import { usePending } from '@/react/hooks/usePending';
import { useEffectEvent } from '@/react/hooks/useEffectEvent';
import { refMerge } from '@/react/utils/ref-utils/refMerge';
import { refAssign } from '@/react/utils/ref-utils/refAssign';
import { EnvironmentService } from '@/services/isomorphic/EnvironmentService';
import type { MaybePromise } from '@/type-utils';
import type IStylable from '../../traits/IStylable';

import { FormProvider } from './FormProvider';

export interface IFormProps<T extends IForm> extends IStylable {
  /** The form model that sets the model for this Form component. */
  form: T;

  /**
   * The submit trigger for the form. This contains the original callback
   * as well as hooked functionality added in the {@link Form} component itself.
   * @param [event] - The submit event from triggering the button.
   * @returns `void` or a promise that resolves to `void`.
   */
  submit: (
    event?: SyntheticEvent<HTMLFormElement, SubmitEvent>
  ) => MaybePromise<void>;

  /**
   * Allows you to make a user interaction event when the form is submitted.
   *
   * This will allow the use of the {@link UserInteractionService} to make
   * analytics events in GTM, Coveo, etc..
   */
  submitInteractionDetails?: InteractionDetails;

  /**
   * This does the same as the above, but makes an interaction event when the user enters
   * the form.
   */
  enterInteractionDetails?: InteractionDetails;

  /**
   * This interaction fires when there is an error on submit validation.
   */
  errorInteractionDetails?: InteractionDetails;

  /**
   * This interaction fires when the form is submitted successfully.
   */
  successInteractionDetails?: InteractionDetails;

  /**
   * If not included uses the generic error handling provided in the `Form` component.
   * If included then error handling must be provided by the wrapping component through
   * the submit callback.
   *
   * Think carefully before using this and if adding another feature would make more sense than
   * using something totally custom.
   */
  useCustomErrorHandling?: boolean;

  /** The component children to be rendered inside the form provider and form component. */
  children: ReactNode;

  /** The callback to execute when the form is changed. */
  onChange?: FormEventHandler<HTMLFormElement>;
}

/**
 * The base Form component, this takes in a `FormModel`, a submit callback, and interaction
 * details and wraps children components with an HTML form element and a provider that
 * supplies the children with these items.
 * This allows us to hook the submit button and use interaction details in parallel
 * to make calls to the `UserInteractionService`.
 */
export const Form = <T extends IForm>(
  props: IFormProps<T>
): ReactElement<unknown> => {
  const {
    className,
    form,
    submit,
    submitInteractionDetails,
    enterInteractionDetails,
    errorInteractionDetails,
    successInteractionDetails,
    useCustomErrorHandling = false,
    onChange,
    children
  } = props;

  const formRef = useRef<HTMLFormElement>(null);
  const [isPending, addPending] = usePending();

  const handleSubmit = async (
    event: SyntheticEvent<HTMLFormElement, SubmitEvent>
  ): Promise<void> => {
    event.preventDefault();

    /** Performs the actual submit action. */
    async function performSubmit(): Promise<void> {
      // This is the stock error handling.
      if (!useCustomErrorHandling) {
        // Keep in mind 'validate' will 'touch' all required form fields.
        const { isValid, invalidProperties } = form.validate;

        const mappedErrors: IFormErrors = {};

        invalidProperties.forEach((ele: IInvalidProperty<T>) => {
          mappedErrors[ele.propertyName as keyof IFormErrors] = ele.errors;
        });

        // Does not submit if invalid.
        if (isValid) {
          // If submission interaction details have been provided, make the action.
          if (submitInteractionDetails)
            UserInteractionService.fireAndForgetAction(
              submitInteractionDetails
            );

          await submit(event);
          // If success interaction has been provided it will fire after the submit completes.
          if (successInteractionDetails)
            UserInteractionService.fireAndForgetAction(
              successInteractionDetails
            );
        } else {
          // If error interaction details have been provided, make the action.
          if (errorInteractionDetails)
            UserInteractionService.fireAndForgetAction(errorInteractionDetails);

          // Scroll back to the form after validation fails on submit.
          formRef.current?.scrollIntoView();
        }
      } else {
        // Custom error handling means that the submit method should contain all error handling.
        await submit(event);
        // If success interaction has been provided it will fire after the submit completes.
        if (successInteractionDetails)
          UserInteractionService.fireAndForgetAction(successInteractionDetails);
      }
    }

    addPending(performSubmit());
  };

  const focusInEffectEvent = useEffectEvent(
    function fireEnterInteractionDetails() {
      if (enterInteractionDetails) {
        UserInteractionService.fireAndForgetAction(enterInteractionDetails);
      }
    }
  );

  // Handles the focusin event on the form. It triggers only once and will send
  // whatever 'userInteraction' event is assigned to the form.
  useEffect(function attachFocusInListener() {
    const elt = formRef.current;

    elt?.addEventListener('focusin', focusInEffectEvent, { once: true });

    return (): void => elt?.removeEventListener('focusin', focusInEffectEvent);
  }, []);

  const formRefs = useMemo(() => {
    return refMerge<HTMLFormElement>((ref) => {
      // In non-production environments, throw an error if the form is nested inside another form.
      // React will call this callback again with null to cleanup the ref if an error occurs
      if (!(process.env.NEXT_PUBLIC_APP_ENV === "prod") && ref?.parentElement?.closest('form')) {
        throw new Error('Form cannot be nested inside another form');
      }
      return () => {};
    }, refAssign(formRef));
  }, []);

  return (
    <FormProvider formModel={form} isLoading={isPending}>
      <form
        ref={formRefs}
        className={className}
        onSubmit={handleSubmit}
        noValidate
        onChange={onChange}
      >
        {children}
      </form>
    </FormProvider>
  );
};
